import md5 from 'md5';
import { combineReducers } from 'redux';
import { handleActions } from 'redux-actions';
import { CALL_API } from 'redux-api-middleware';
import { PHARMACY_API } from '../../../../config/endpoints';
import {
  CHECKOUT_SKIP_EVIDENCE,
  CHECKOUT_UPLOAD_EVIDENCE,
} from '../../../../server/services/analytics-events';
import paths from '../../../routes/paths';
import { getAPIErrorMessage } from '../../api-error';
import { TRACK_EVENT } from '../../middleware/analytics';
import { CHAIN_NEXT, chainAPIActions } from '../../middleware/chain';
import { ROUTE } from '../../middleware/route';
import { validate } from '../../middleware/validator';

// ACTIONS

export const FETCH_INVOICE_REQUEST = 'CHECKOUT/FETCH_INVOICE_REQUEST';
export const FETCH_INVOICE_RECEIVE = 'CHECKOUT/FETCH_INVOICE_RECEIVE';
export const FETCH_INVOICE_FAILURE = 'CHECKOUT/FETCH_INVOICE_FAILURE';
export const SELECT_EXEMPTION = 'CHECKOUT/SELECT_EXEMPTION';
export const NO_EXEMPTION_SELECTED = 'CHECKOUT/NO_EXEMPTION_SELECTED';
export const CLEAR_EXEMPTION_SELECTION_ERROR =
  'CHECKOUT/CLEAR_EXEMPTION_SELECTION_ERROR';

export const SUBMIT_EXEMPTION_REQUEST = 'CHECKOUT/SUBMIT_EXEMPTION_REQUEST';
export const SUBMIT_EXEMPTION_RECEIVE = 'CHECKOUT/SUBMIT_EXEMPTION_RECEIVE';
export const SUBMIT_EXEMPTION_FAILURE = 'CHECKOUT/SUBMIT_EXEMPTION_FAILURE';

export const CLEAR_SUBMIT_DECLARATION_ERROR =
  'CHECKOUT/CLEAR_SUBMIT_DECLARATION_ERROR';

export const SET_EVIDENCE_SKIPPED = 'CHECKOUT/SET_EVIDENCE_SKIPPED';
export const SET_EVIDENCE_URL = 'CHECKOUT/SET_EVIDENCE_URL';

export const PRESIGNED_PUT_URL_REQUEST = 'CHECKOUT/PRESIGNED_PUT_URL_REQUEST';
export const PRESIGNED_PUT_URL_RECEIVE = 'CHECKOUT/PRESIGNED_PUT_URL_RECEIVE';
export const PRESIGNED_PUT_URL_FAILURE = 'CHECKOUT/PRESIGNED_PUT_URL_FAILURE';

export const PRESIGNED_GET_URL_REQUEST = 'CHECKOUT/PRESIGNED_GET_URL_REQUEST';
export const PRESIGNED_GET_URL_RECEIVE = 'CHECKOUT/PRESIGNED_GET_URL_RECEIVE';
export const PRESIGNED_GET_URL_FAILURE = 'CHECKOUT/PRESIGNED_GET_URL_FAILURE';

export const EVIDENCE_UPLOAD_REQUEST = 'CHECKOUT/EVIDENCE_UPLOAD_REQUEST';
export const EVIDENCE_UPLOAD_RECEIVE = 'CHECKOUT/EVIDENCE_UPLOAD_RECEIVE';
export const EVIDENCE_UPLOAD_FAILURE = 'CHECKOUT/EVIDENCE_UPLOAD_FAILURE';

export const PAY_INVOICE_REQUEST = 'CHECKOUT/PAY_INVOICE_REQUEST';
export const PAY_INVOICE_RECEIVE = 'CHECKOUT/PAY_INVOICE_RECEIVE';
export const PAY_INVOICE_FAILURE = 'CHECKOUT/PAY_INVOICE_FAILURE';

export const FETCH_BRAINTREE_TOKEN_REQUEST =
  'CHECKOUT/FETCH_BRAINTREE_TOKEN_REQUEST';
export const FETCH_BRAINTREE_TOKEN_RECEIVE =
  'CHECKOUT/FETCH_BRAINTREE_TOKEN_RECEIVE';
export const FETCH_BRAINTREE_TOKEN_FAILURE =
  'CHECKOUT/FETCH_BRAINTREE_TOKEN_FAILURE';

export const CLEAR_CHECKOUT_FLOW = 'CHECKOUT/CLEAR_CHECKOUT_FLOW';
export const CLEAR_PAYMENT_ERROR = 'CHECKOUT/CLEAR_PAYMENT_ERROR';
export const SET_CHECKBOX_ISVISIBLE = 'CHECKOUT/SET_CHECKBOX_ISVISIBLE';
export const START_CHECKOUT_FLOW = 'CHECKOUT/START_CHECKOUT_FLOW';

// ACTION CREATORS

export const startPaymentFlow = (payload) => ({
  type: START_CHECKOUT_FLOW,
  meta: { [ROUTE]: payload?.clickCollect?.eligible ? paths.checkout.paymentDeliveryPreference : paths.checkout.paymentMethod }
})

export const fetchInvoiceForOrder = (orderId, apiEndpoint = PHARMACY_API) => ({
  [CALL_API]: {
    endpoint: `${apiEndpoint}/public/orders/${orderId}/checkoutdata`,
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    types: [
      FETCH_INVOICE_REQUEST,
      {
        type: FETCH_INVOICE_RECEIVE,
        [CHAIN_NEXT]: ({ payload }) => startPaymentFlow(payload)
      },
      { type: FETCH_INVOICE_FAILURE, meta: { [ROUTE]: '/' } },
    ],
    options: { addAuth: true },
  },
});

export const submitExemption = (
  orderId,
  code,
  skippedEvidence,
  ccPharmacySelected,
  apiEndpoint = PHARMACY_API
) => ({
  [CALL_API]: {
    endpoint: `${apiEndpoint}/public/orders/S${orderId}/exemption`,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      exemptionId: code,
      skippedEvidence,
      storeId: ccPharmacySelected?.id,
    }),
    types: [
      SUBMIT_EXEMPTION_REQUEST,
      {
        type: SUBMIT_EXEMPTION_RECEIVE,
        [ROUTE]: paths.checkout.complete,
      },
      {
        type: SUBMIT_EXEMPTION_FAILURE,
        [ROUTE]: paths.checkout.declaration,
      },
    ],
    options: { addAuth: true },
  },
});

export const selectExemption = (code) => ({
  type: 'SELECT_EXEMPTION_VALIDATION',
  [validate]: {
    validator: () => code != null && code.length === 1,
    success: {
      type: SELECT_EXEMPTION,
      code,
      [ROUTE]: paths.checkout.uploadEvidence,
    },
    fail: { type: NO_EXEMPTION_SELECTED },
  },
});

export const clearExemptionSelectionError = () => ({
  type: CLEAR_EXEMPTION_SELECTION_ERROR,
});

export const setEvidenceSkipped = (skipped) => ({
  type: SET_EVIDENCE_SKIPPED,
  skipped,
  [ROUTE]: skipped ? paths.checkout.declaration : null,
  [TRACK_EVENT]: skipped ? CHECKOUT_SKIP_EVIDENCE : null,
});

export const resetEvidenceSkipped = () => setEvidenceSkipped(false);

// md5 hashes the data and converts it to base64
const toBase64 = (str) => {
  if (global.btoa) return btoa(str);
  return Buffer.from(str, 'binary').toString('base64');
};
export const getEvidenceMD5 = (evidence) =>
  toBase64(
    md5(evidence) // returns a hex-encoded string
      .match(/\w{2}/g) // extract pairs of hex digits (1 byte)
      .map((h) => parseInt(h, 16)) // convert to number
      .map((b) => String.fromCharCode(b)) // convert to binary string characters
      .join('') // join into a binary string
  );

export const getEvidenceGetURL = (orderId, apiEndpoint = PHARMACY_API) => ({
  [CALL_API]: {
    endpoint: `${apiEndpoint}/public/orders/S${orderId}/exemption/evidence`,
    method: 'GET',
    types: [
      PRESIGNED_GET_URL_REQUEST,
      PRESIGNED_GET_URL_RECEIVE,
      PRESIGNED_GET_URL_FAILURE,
    ],
    options: { addAuth: true },
  },
});

const getEvidencePutURL = (orderId, evidence, apiEndpoint = PHARMACY_API) => ({
  [CALL_API]: {
    endpoint: `${apiEndpoint}/public/orders/S${orderId}/exemption/evidence`,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      md5: getEvidenceMD5(evidence),
    }),
    types: [
      PRESIGNED_PUT_URL_REQUEST,
      PRESIGNED_PUT_URL_RECEIVE,
      PRESIGNED_PUT_URL_FAILURE,
    ],
    options: { addAuth: true },
  },
});

const putEvidence = (evidence, apiEndpoint) => ({
  [CALL_API]: {
    endpoint: apiEndpoint,
    method: 'PUT',
    headers: {
      'Content-MD5': getEvidenceMD5(evidence),
    },
    body: evidence,
    types: [
      {
        type: EVIDENCE_UPLOAD_REQUEST,
        [TRACK_EVENT]: CHECKOUT_UPLOAD_EVIDENCE,
      },
      { type: EVIDENCE_UPLOAD_RECEIVE, [ROUTE]: paths.checkout.viewEvidence },
      EVIDENCE_UPLOAD_FAILURE,
    ],
  },
});

export const uploadEvidence = (orderId, evidence, apiEndpoint = PHARMACY_API) =>
  chainAPIActions(
    getEvidencePutURL(orderId, evidence, apiEndpoint),
    (payload) => putEvidence(evidence, payload.url)
  );

export const clearSubmitDeclarationError = () => ({
  type: CLEAR_SUBMIT_DECLARATION_ERROR,
});

export const clearCheckoutFlow = () => ({
  type: CLEAR_CHECKOUT_FLOW,
});

export const clearPaymentError = () => ({
  type: CLEAR_PAYMENT_ERROR,
  [ROUTE]: paths.checkout.paymentDetails,
});

export const payInvoice = (
  orderId,
  nonce,
  ccPharmacySelected,
  apiEndpoint = PHARMACY_API
) => {
  // The public endpoint requires a public orderId.
  // Requests are the format R{orderId}
  // Orders are the format S{orderId}
  // Payment completion is being attempted so the id should represent an order
  // TODO: The long term fix is to start the checkout process is using the public/order/checkoutdata
  const publicOrderId = `S${orderId}`;
  return {
    [CALL_API]: {
      endpoint: `${apiEndpoint}/public/orders/${publicOrderId}/payment`,
      method: 'POST',
      body: JSON.stringify({
        token: nonce,
        storeId: ccPharmacySelected?.id,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
      types: [
        PAY_INVOICE_REQUEST,
        {
          type: PAY_INVOICE_RECEIVE,
          [ROUTE]: paths.checkout.complete,
        },
        PAY_INVOICE_FAILURE,
      ],
      options: { addAuth: true },
    },
  };
};

export const fetchBraintreeToken = (apiEndpoint = PHARMACY_API) => ({
  [CALL_API]: {
    endpoint: `${apiEndpoint}/public/me/braintreetokens`,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    types: [
      FETCH_BRAINTREE_TOKEN_REQUEST,
      FETCH_BRAINTREE_TOKEN_RECEIVE,
      FETCH_BRAINTREE_TOKEN_FAILURE,
    ],
    options: { addAuth: true },
  },
});

export const setChecked = (isChecked) => ({
  type: SET_CHECKBOX_ISVISIBLE,
  payload: isChecked,
});
// SELECTORS
export const getInvoice = (state) => state.invoice;
export const getInvoiceId = (state) => state.invoice.id || null;
export const getOrderId = (state) => state.invoice.orderId || null;
export const getInvoiceNeedsPaying = (state) => state.invoice.status === 'sent';
export const getIsInvoiceSettled = (state) => state.invoice.status === 'paid';
export const getAmountOutstanding = (state) =>
  state.invoice.amountOutstanding || 0;
export const getLineItems = (state) => state.invoice.lineItems || [];
export const getSelectedExemptionCode = (state) => state.selectedExemptionCode;
export const getErrorSelectingExemptionCode = (state) =>
  state.errorSelectingExemptionCode;
export const getIsEvidenceSkipped = (state) => state.evidenceSkipped;
export const getErrorUploadingEvidence = () => null;
export const getEvidenceURL = (state) => state.evidenceURL;
export const getIsEvidenceUploaded = (state) => state.evidenceUploaded;
export const getSelectedExemptionNeedsEvidence = (state) =>
  state.selectedExemptionCode != null && state.selectedExemptionCode !== 'A';
export const getIsSubmittable = (state) => {
  if (!getSelectedExemptionCode(state)) return false;
  if (!getSelectedExemptionNeedsEvidence(state)) return true;
  if (getIsEvidenceSkipped(state)) return true;
  if (getIsEvidenceUploaded(state)) return true;
  return false;
};
export const getSubmitDeclarationError = (state) =>
  state.submitDeclarationError;
export const getIsSubmittingDeclaration = (state) =>
  state.isSubmittingDeclaration;
export const getBraintreeToken = (state) => state.braintreeToken;
export const getPaymentError = (state) => state.paymentError;
export const getCheckbox = (state) => state?.checkboxStatus;

// REDUCERS
const invoice = handleActions(
  {
    [FETCH_INVOICE_RECEIVE]: (state, action) =>
      action.payload && action.payload.invoice,
    [SUBMIT_EXEMPTION_RECEIVE]: (state) => ({ ...state, status: 'paid' }),
    [PAY_INVOICE_RECEIVE]: (state) => ({ ...state, status: 'paid' }),
    [CLEAR_CHECKOUT_FLOW]: () => ({}),
  },
  {}
);

const isSubmittingDeclaration = handleActions(
  {
    [SUBMIT_EXEMPTION_REQUEST]: () => true,
    [SUBMIT_EXEMPTION_FAILURE]: () => false,
    [SUBMIT_EXEMPTION_RECEIVE]: () => false,
    [CLEAR_CHECKOUT_FLOW]: () => false,
    [FETCH_INVOICE_RECEIVE]: () => false,
  },
  false
);

const submitDeclarationError = handleActions(
  {
    [CLEAR_SUBMIT_DECLARATION_ERROR]: () => null,
    [SUBMIT_EXEMPTION_REQUEST]: () => null,
    [SUBMIT_EXEMPTION_FAILURE]: () => 'Something went wrong',
    [CLEAR_CHECKOUT_FLOW]: () => null,
    [FETCH_INVOICE_RECEIVE]: () => null,
  },
  null
);

const selectedExemptionCode = handleActions(
  {
    [SELECT_EXEMPTION]: (state, action) => action.code,
    [NO_EXEMPTION_SELECTED]: () => null,
    [CLEAR_CHECKOUT_FLOW]: () => null,
    [FETCH_INVOICE_RECEIVE]: () => null,
    [PAY_INVOICE_RECEIVE]: () => null,
  },
  null
);

const errorSelectingExemptionCode = handleActions(
  {
    [SELECT_EXEMPTION]: () => false,
    [NO_EXEMPTION_SELECTED]: () => true,
    [CLEAR_EXEMPTION_SELECTION_ERROR]: () => false,
    [CLEAR_CHECKOUT_FLOW]: () => false,
    [FETCH_INVOICE_RECEIVE]: () => false,
  },
  false
);

const evidenceSkipped = handleActions(
  {
    [SET_EVIDENCE_SKIPPED]: (state, action) => action.skipped === true,
    [CLEAR_CHECKOUT_FLOW]: () => false,
    [FETCH_INVOICE_RECEIVE]: () => false,
  },
  false
);

const evidenceUploaded = handleActions(
  {
    [EVIDENCE_UPLOAD_RECEIVE]: () => true,
    [SELECT_EXEMPTION]: () => false,
    [CLEAR_CHECKOUT_FLOW]: () => false,
    [FETCH_INVOICE_RECEIVE]: () => false,
  },
  false
);

const evidenceURL = handleActions(
  {
    [PRESIGNED_GET_URL_RECEIVE]: (state, action) => action.payload.url,
    [FETCH_INVOICE_RECEIVE]: () => null,
  },
  null
);

const paymentError = handleActions(
  {
    [PAY_INVOICE_FAILURE]: (state, action) =>
      getAPIErrorMessage(action) || 'Something went wrong',
    [FETCH_INVOICE_RECEIVE]: () => null,
    [PAY_INVOICE_RECEIVE]: () => null,
    [CLEAR_CHECKOUT_FLOW]: () => null,
    [CLEAR_PAYMENT_ERROR]: () => null,
  },
  null
);

const braintreeToken = handleActions(
  {
    [FETCH_BRAINTREE_TOKEN_REQUEST]: () => null,
    [FETCH_BRAINTREE_TOKEN_FAILURE]: () => null,
    [FETCH_BRAINTREE_TOKEN_RECEIVE]: (state, action) => action.payload.token,
    [FETCH_INVOICE_RECEIVE]: () => null,
    [CLEAR_CHECKOUT_FLOW]: () => null,
  },
  null
);

const checkboxStatus = handleActions(
  {
    [SET_CHECKBOX_ISVISIBLE]: (state, action) => action?.payload,
  },
  false
);

export default combineReducers({
  invoice,
  isSubmittingDeclaration,
  selectedExemptionCode,
  errorSelectingExemptionCode,
  submitDeclarationError,
  evidenceSkipped,
  evidenceUploaded,
  evidenceURL,
  paymentError,
  braintreeToken,
  checkboxStatus,
});
